import {
  Alert,
  AlertDescription,
  AlertTitle,
  Avatar,
  AvatarFallback,
  AvatarImage,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
  Label,
  Spinner,
} from "@getgreenline/blaze-ui";
import { Search, TriangleAlert } from "lucide-react";
import { useState } from "react";
import { useBrandsCatalog, type Brand } from "@/hooks/use-brands-catalog";
import { useDebouncedValue } from "@/hooks/use-debounced-value";
import { getInitials } from "@/utils/strings";
import { BrandUserAssignmentForm } from "./brand-user-assignment-form";

type AddBrandDialogProps = {
  onboardedBrandIds: string[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

export function AddBrandDialog({
  onboardedBrandIds,
  open,
  onOpenChange,
}: AddBrandDialogProps) {
  const [query, setQuery] = useState("");
  const [selectedBrand, setSelectedBrand] = useState<Brand | null>(null);
  const debouncedQuery = useDebouncedValue(query, 300);
  const hasInput = query.trim().length > 0;
  const hasQuery = debouncedQuery.trim().length > 0;
  const isDebouncing = query.trim() !== debouncedQuery.trim();
  const catalog = useBrandsCatalog(debouncedQuery, {
    enabled: open && !selectedBrand && hasQuery,
  });
  const onboardedIds = new Set(onboardedBrandIds);
  const availableBrands = (catalog.data ?? []).filter(
    (brand) => !onboardedIds.has(brand.id),
  );

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) {
      setQuery("");
      setSelectedBrand(null);
    }
    onOpenChange(nextOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="tw:max-w-2xl">
        {selectedBrand ? (
          <BrandUserAssignmentForm
            brand={selectedBrand}
            cancelLabel="Back"
            onCancel={() => setSelectedBrand(null)}
            onSuccess={() => handleOpenChange(false)}
          />
        ) : (
          <div className="tw:space-y-5">
            <DialogHeader>
              <DialogTitle>Add brand</DialogTitle>
              <DialogDescription>
                Search the brand catalog, then assign or invite its first user.
              </DialogDescription>
            </DialogHeader>

            <div className="tw:space-y-2">
              <Label htmlFor="brand-catalog-search">Search brands</Label>
              <InputGroup>
                <InputGroupAddon align="inline-start">
                  <Search className="tw:size-4" aria-hidden="true" />
                </InputGroupAddon>
                <InputGroupInput
                  id="brand-catalog-search"
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  placeholder="Search by brand name"
                  autoComplete="off"
                />
                {isDebouncing || catalog.isFetching ? (
                  <InputGroupAddon align="inline-end">
                    <Spinner className="tw:size-4" />
                    <span className="tw:sr-only">Searching brands</span>
                  </InputGroupAddon>
                ) : null}
              </InputGroup>
            </div>

            <div className="tw:overflow-hidden tw:rounded-lg tw:border tw:bg-background">
              {!hasInput ? (
                <p className="tw:p-4 tw:text-sm tw:text-muted-foreground">
                  Enter a brand name to search the catalog.
                </p>
              ) : null}

              {hasInput &&
              (isDebouncing || catalog.isLoading || catalog.isFetching) ? (
                <p className="tw:p-4 tw:text-sm tw:text-muted-foreground">
                  Searching brands…
                </p>
              ) : null}

              {hasInput && !isDebouncing && catalog.isError ? (
                <Alert
                  variant="destructive"
                  className="tw:rounded-none tw:border-0"
                >
                  <TriangleAlert className="tw:size-4" />
                  <AlertTitle>Brand search failed</AlertTitle>
                  <AlertDescription>
                    Try the search again. If the problem continues, contact
                    support.
                  </AlertDescription>
                </Alert>
              ) : null}

              {hasQuery &&
              !isDebouncing &&
              catalog.isSuccess &&
              availableBrands.length === 0 ? (
                <p className="tw:p-4 tw:text-sm tw:text-muted-foreground">
                  No available brands found for “{debouncedQuery.trim()}”.
                </p>
              ) : null}

              {hasQuery &&
              !isDebouncing &&
              catalog.isSuccess &&
              availableBrands.length > 0 ? (
                <ul className="tw:max-h-80 tw:divide-y tw:overflow-y-auto">
                  {availableBrands.map((brand) => (
                    <li key={brand.id}>
                      <button
                        type="button"
                        className="tw:flex tw:w-full tw:items-center tw:gap-3 tw:px-4 tw:py-3 tw:text-left tw:text-foreground tw:transition-colors hover:tw:bg-muted/50 focus-visible:tw:outline-none focus-visible:tw:ring-2 focus-visible:tw:ring-ring focus-visible:tw:ring-inset"
                        onClick={() => setSelectedBrand(brand)}
                      >
                        <Avatar className="tw:size-10 tw:shrink-0">
                          {brand.avatarImageUrl ? (
                            <AvatarImage
                              src={brand.avatarImageUrl}
                              alt={`${brand.name} icon`}
                            />
                          ) : null}
                          <AvatarFallback>
                            {getInitials(brand.name)}
                          </AvatarFallback>
                        </Avatar>
                        <span className="tw:min-w-0 tw:flex-1">
                          <span className="tw:block tw:truncate tw:font-medium">
                            {brand.name}
                          </span>
                          <span className="tw:block tw:truncate tw:text-xs tw:text-muted-foreground">
                            {brand.country ?? "Country unavailable"}
                          </span>
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              ) : null}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
